import React from "react";
import { type SavedMapPlotRequestDetail } from "@/features/map/types";
import {
  type GridColDef,
  type GridSelectionModel,
  type GridCellEditCommitParams,
} from "@mui/x-data-grid-pro";
import { styled } from "@mui/material/styles";
import { StyledDataGrid as BaseStyledDataGrid } from "@/components/DataGrid/ChibanKaokuNumberSearchStyleDataGrid";
import { format, parseISO } from "date-fns";
import { Box, Button, Checkbox, IconButton, Tooltip } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import { TagGrid } from "@/features/map/components/TagGrid";
import { EditableCell } from "@/features/mapPlotRequest/components/EditableCell";
import { EditableSelectCell } from "@/features/mapPlotRequest/components/EditableSelectCell";
import { CoordinatesCell } from "@/features/mapPlotRequest/components/CoordinatesCell";
import { formatMapPlotRequestDateTime } from "@/features/mapPlotRequest/utils/dateTime";
import { useAdditionalFieldsConfig } from "@/features/map/hooks/useAdditionalFieldsConfig";
import { parseAdditionalFields } from "@/features/map/utils/additionalFieldsHelpers";

// パフォーマンス最適化のため、パース済みのadditionalFieldsを含む型
type SavedMapPlotRequestDetailWithParsed = SavedMapPlotRequestDetail & {
  _parsedAdditionalFields?: Record<string, string>;
};

interface IProps {
  plotRequestId: number;
  details: SavedMapPlotRequestDetailWithParsed[];
  page: number;
  setPage: React.Dispatch<React.SetStateAction<number>>;
  pageSize: number;
  setPageSize: React.Dispatch<React.SetStateAction<number>>;
  selectedIds: GridSelectionModel;
  onSelectionChange: (newSelection: GridSelectionModel) => void;
  onCellEdit: (params: GridCellEditCommitParams) => void;
  onCoordinatesEdit: (
    id: number,
    latitude: number | null,
    longitude: number | null
  ) => void;
  availableTags: Array<{ id: number; name: string }>;
  onTagEdit: (id: number, tags: Array<{ id: number; name: string }>) => void;
  onOpenFilesPage: (detail: SavedMapPlotRequestDetail) => void;
  onAdditionalFieldsEdit?: (
    id: number,
    additionalFields: Record<string, any> | null
  ) => void;
}

const StyledDataGrid = styled(BaseStyledDataGrid)`
  // テーブルのセルをクリックしても青い枠が表示されないようにする
  & .MuiDataGrid-cell:focus-within,
  & .MuiDataGrid-cell:focus {
    outline: none !important;
  }
`;

export const MapPlotRequestDetailGrid: React.FC<IProps> = ({
  plotRequestId,
  details,
  page,
  setPage,
  pageSize,
  setPageSize,
  selectedIds,
  onSelectionChange,
  onCellEdit,
  onCoordinatesEdit,
  availableTags,
  onTagEdit,
  onOpenFilesPage,
  onAdditionalFieldsEdit,
}) => {
  const gridRef = React.useRef<HTMLDivElement>(null);
  const { availableFields } = useAdditionalFieldsConfig();

  /**
   * 編集開始時に該当行を画面中央にスクロールする
   *
   * @param rowId - 行ID
   * @param _field - フィールド名（未使用）
   * @param isEditing - 編集中かどうか
   */
  const handleEditingChange = (
    rowId: number,
    _field: string,
    isEditing: boolean
  ): void => {
    if (isEditing) {
      // 編集開始時、少し遅延させてから該当行が見える位置にスクロール
      setTimeout(() => {
        const rowElement = gridRef.current?.querySelector(
          `[data-id="${rowId}"]`
        ) as HTMLElement;
        if (rowElement) {
          rowElement.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
        }
      }, 100);
    }
  };

  /**
   * ページ番号を変更する
   *
   * @param newPage - 新しいページ番号
   */
  const handlePageChange = (newPage: number): void => {
    setPage(newPage);
  };

  /**
   * 1ページあたりの表示件数を変更する
   *
   * @param newPageSize - 新しい表示件数
   */
  const handlePageSizeChange = (newPageSize: number): void => {
    setPageSize(newPageSize);
  };

  const columns: Array<GridColDef<SavedMapPlotRequestDetailWithParsed>> = [
    {
      field: "status",
      headerName: "ステータス",
      width: 120,
      sortable: true,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => {
        if (params.value === "SUCCEED") {
          return "成功";
        } else if (params.value === "FAILED") {
          return "失敗";
        }
        return String(params.value);
      },
    },
    {
      field: "realEstateType",
      headerName: "種別",
      width: 120,
      sortable: true,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => {
        const bookType = params.value as
          | "土地"
          | "建物"
          | "区分建物"
          | null
          | undefined;

        return (
          <EditableSelectCell
            value={bookType ?? ""}
            displayValue={bookType ?? ""}
            rowId={params.row.id}
            field="realEstateType"
            options={[
              { value: "土地", label: "土地" },
              { value: "建物", label: "建物" },
              { value: "区分建物", label: "区分建物" },
            ]}
            onEdit={onCellEdit}
            disabled={params.row.status === "FAILED"}
            onEditingChange={handleEditingChange}
          />
        );
      },
    },
    {
      field: "requestAddress",
      headerName: "住居表示",
      width: 250,
      sortable: true,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => {
        const value = params.value as string | null;
        return (
          <EditableCell
            value={value}
            rowId={params.row.id}
            field="requestAddress"
            onEdit={onCellEdit}
            disabled={params.row.status === "FAILED"}
            onEditingChange={handleEditingChange}
            maxLength={256}
          />
        );
      },
    },
    {
      field: "requestLocation",
      headerName: "所在",
      width: 200,
      sortable: true,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => {
        const value = params.value as string | null;
        return (
          <EditableCell
            value={value}
            rowId={params.row.id}
            field="requestLocation"
            onEdit={onCellEdit}
            disabled={params.row.status === "FAILED"}
            onEditingChange={handleEditingChange}
            maxLength={256}
          />
        );
      },
    },
    {
      field: "chibanOrKaokuNumber",
      headerName: "地番・家屋番号",
      width: 200,
      sortable: false, // DBに存在しない仮想フィールド(requestChiban/requestKaokuNumberを種別で切り替え表示)のためソート無効
      align: "center",
      headerAlign: "center",
      renderCell: (params) => {
        const bookType = params.row.realEstateType ?? "土地";
        const isBuilding = bookType === "建物" || bookType === "区分建物";
        const value = isBuilding
          ? params.row.requestKaokuNumber
          : params.row.requestChiban;
        const field = isBuilding ? "requestKaokuNumber" : "requestChiban";

        return (
          <EditableCell
            value={value ?? ""}
            rowId={params.row.id}
            field={field}
            onEdit={onCellEdit}
            disabled={params.row.status === "FAILED"}
            onEditingChange={handleEditingChange}
            maxLength={256}
          />
        );
      },
    },
    {
      field: "coordinates",
      headerName: "緯度・経度",
      width: 200,
      sortable: false,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => {
        return (
          <CoordinatesCell
            latitude={params.row.substituteLatitude}
            longitude={params.row.substituteLongitude}
            rowId={params.row.id}
            onEdit={(newLatitude, newLongitude) => {
              onCoordinatesEdit?.(params.row.id, newLatitude, newLongitude);
            }}
            disabled={params.row.status === "FAILED"}
            onEditingChange={handleEditingChange}
          />
        );
      },
    },
    {
      field: "tags",
      headerName: "タグ",
      width: 220,
      sortable: false,
      align: "left",
      headerAlign: "center",
      renderCell: (params) => {
        const tags = params.row.tags as Array<{ id: number; name: string }>;
        const tagNames = tags?.map((tag) => tag.name) ?? [];
        const isFailed = params.row.status === "FAILED";

        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 0.5,
              width: "100%",
              py: 1,
              overflow: "hidden",
            }}
          >
            <Box sx={{ flexGrow: 1, minWidth: 0, overflow: "hidden" }}>
              {tagNames.length > 0 && <TagGrid tags={tagNames} />}
            </Box>
            {!isFailed && (
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onTagEdit(params.row.id, tags || []);
                }}
                sx={{
                  flexShrink: 0,
                  padding: "4px",
                  ml: 1,
                }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            )}
          </Box>
        );
      },
    },
    {
      field: "filesAction",
      headerName: "資料",
      width: 120,
      sortable: false,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => {
        const isFailed = params.row.status === "FAILED";

        return (
          <Button
            variant="outlined"
            size="small"
            startIcon={<AttachFileIcon fontSize="small" />}
            onClick={(event) => {
              event.stopPropagation();
              onOpenFilesPage(params.row);
            }}
            disabled={isFailed}
          >
            資料
          </Button>
        );
      },
    },
    {
      field: "remarks",
      headerName: "備考",
      width: 250,
      sortable: false,
      align: "left",
      headerAlign: "center",
      renderCell: (params) => {
        const remarks = params.value as string | null;
        const displayMaxLength = 50;
        const isTextTooLong =
          remarks !== null && remarks.length > displayMaxLength;
        const displayText = isTextTooLong
          ? `${remarks.substring(0, displayMaxLength)}...`
          : remarks;

        return (
          <Tooltip title={isTextTooLong ? remarks : ""} arrow placement="top">
            <Box sx={{ width: "100%" }}>
              <EditableCell
                value={remarks}
                displayValue={displayText}
                rowId={params.row.id}
                field="remarks"
                onEdit={onCellEdit}
                disabled={params.row.status === "FAILED"}
                onEditingChange={handleEditingChange}
                multiline={true}
                maxLength={200}
              />
            </Box>
          </Tooltip>
        );
      },
    },
  ];

  // カスタム項目の各フィールドを個別の列として生成
  const additionalFieldsColumns: Array<
    GridColDef<SavedMapPlotRequestDetailWithParsed>
  > = React.useMemo(() => {
    if (!availableFields?.fields) {
      return [];
    }

    return availableFields.fields.map((field) => {
      // 日時フィールドのみ幅を広げる（カレンダーUIがはみ出ないようにするため）
      const columnWidth = field.fieldType === "datetime" ? 220 : 180;

      return {
        field: `additionalField_${field.id}`,
        headerName: field.key,
        width: columnWidth,
        sortable: false,
        align: "left",
        headerAlign: "center",
        renderCell: (params) => {
          // 親コンポーネントで事前にパース済みのデータを使用（パフォーマンス最適化）
          const parsedFields =
            params.row._parsedAdditionalFields ??
            parseAdditionalFields(params.row.additionalFields);
          const value = parsedFields[field.id] ?? "";

          /**
           * 真偽値フィールドの値を更新する
           *
           * @param checked - チェックボックスの状態
           */
          const handleBooleanEdit = (checked: boolean): void => {
            if (!onAdditionalFieldsEdit) return;

            const newValue = checked ? "true" : "false";
            const updatedFields = {
              ...parsedFields,
              [field.id]: newValue,
            };

            // バックエンド形式に変換（空文字列も送信可能）
            const fieldsToSend: Record<string, { value: string }> = {};
            Object.entries(updatedFields).forEach(([fieldId, val]) => {
              fieldsToSend[fieldId] = {
                value: val,
              };
            });

            const fieldsToUpdate =
              Object.keys(fieldsToSend).length > 0 ? fieldsToSend : null;
            onAdditionalFieldsEdit(params.row.id, fieldsToUpdate);
          };

          // 真偽値フィールドの場合はチェックボックスを表示（空の場合もunchecked表示）
          if (field.fieldType === "boolean") {
            return (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "100%",
                  height: "100%",
                }}
              >
                <Checkbox
                  checked={value === "true"}
                  onChange={(e) => {
                    handleBooleanEdit(e.target.checked);
                  }}
                  disabled={params.row.status === "FAILED"}
                />
              </Box>
            );
          }

          // 日付・日時フィールドの表示用フォーマット
          let displayValue = value;
          if (value && typeof value === "string") {
            try {
              if (field.fieldType === "date") {
                // 日付フィールド: YYYY-MM-DD → YYYY/MM/DD
                const parsed = parseISO(value);
                if (!isNaN(parsed.getTime())) {
                  displayValue = format(parsed, "yyyy/MM/dd");
                }
              } else if (field.fieldType === "datetime") {
                // 日時フィールド: YYYY-MM-DDTHH:mm → YYYY/MM/DD HH:mm
                const parsed = parseISO(value);
                if (!isNaN(parsed.getTime())) {
                  displayValue = format(parsed, "yyyy/MM/dd HH:mm");
                }
              }
            } catch {
              // パースエラーの場合は元の値を使用
              displayValue = value;
            }
          }

          /**
           * 追加項目の値を更新する
           *
           * @param editParams - セル編集のパラメータ
           */
          const handleEdit = (editParams: GridCellEditCommitParams): void => {
            if (!onAdditionalFieldsEdit) return;

            const newValue = String(editParams.value ?? "");
            const updatedFields = {
              ...parsedFields,
              [field.id]: newValue,
            };

            // バックエンド形式に変換（空文字列も送信可能）
            const fieldsToSend: Record<string, { value: string }> = {};
            Object.entries(updatedFields).forEach(([fieldId, val]) => {
              fieldsToSend[fieldId] = {
                value: val,
              };
            });

            const fieldsToUpdate =
              Object.keys(fieldsToSend).length > 0 ? fieldsToSend : null;
            onAdditionalFieldsEdit(params.row.id, fieldsToUpdate);
          };

          // フィールドタイプに応じて最大文字数・桁数を設定
          // テキスト・テキストエリア: 500文字、数値: 15桁（JavaScriptの最大値が9007199254740991の16桁なので、桁数で15桁までとする）
          const maxLength = field.fieldType === "number" ? 15 : 500;

          return (
            <EditableCell
              value={value}
              displayValue={displayValue}
              rowId={params.row.id}
              field={`additionalField_${field.id}`}
              onEdit={handleEdit}
              disabled={params.row.status === "FAILED"}
              onEditingChange={handleEditingChange}
              maxLength={maxLength}
              fieldType={field.fieldType}
            />
          );
        },
      };
    });
  }, [availableFields, onAdditionalFieldsEdit, handleEditingChange]);

  const allColumns: Array<GridColDef<SavedMapPlotRequestDetailWithParsed>> = [
    ...columns,
    ...additionalFieldsColumns,
    {
      field: "createdAt",
      headerName: "作成日時",
      width: 150,
      sortable: true,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => {
        const value = params.value as string | null | undefined;
        return formatMapPlotRequestDateTime(value, "");
      },
    },
    {
      field: "updatedAt",
      headerName: "更新日時",
      width: 150,
      sortable: true,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => {
        const value = params.value as string | null | undefined;
        return formatMapPlotRequestDateTime(value, "");
      },
    },
  ];

  return (
    <Box ref={gridRef} sx={{ height: "100%", width: "100%" }}>
      <StyledDataGrid
        stickyHeader={true}
        disableColumnMenu
        rows={details}
        columns={allColumns}
        checkboxSelection
        disableSelectionOnClick
        selectionModel={selectedIds}
        onSelectionModelChange={onSelectionChange}
        page={page}
        pageSize={pageSize}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        pagination
        rowsPerPageOptions={[10, 25, 50, 100]}
        getRowHeight={() => "auto"}
        initialState={{
          columns: {
            columnVisibilityModel: { id: false },
          },
        }}
      />
    </Box>
  );
};
